import {
  ProForm,
  ProFormCascader,
  ProFormDigit,
  ProFormItem,
  ProFormTextArea,
  ProFormUploadButton,
} from "@ant-design/pro-components";
import { useModel } from "@@/exports";
import { UploadChangeParam } from "antd/lib/upload/interface";
import { YsUploadFile } from "@/utils/types";
import {
  Button,
  Form,
  FormInstance,
  message,
  Modal,
  Upload,
  UploadFile,
  UploadProps,
} from "antd";
import { REGEXP_ORIGIN, UPLOAD_DIR, UPLOAD_DIR_PROJECT } from "@/constant";
import {
  getFileFullSrc,
  getFileSize,
  getVideoCodecInfo,
  isBrowserSupportCodec,
  setFileThumbUrl,
  setFileUploadUrl,
} from "@/utils/file";
import { forwardRef, Ref, useImperativeHandle, useRef, useState } from "react";
import * as obsModuleApi from "@/api/obsModule2048";
import ViewerProps, { ImageDecorator } from "react-viewer/lib/ViewerProps";
import Viewer from "react-viewer";
import {
  MAX_FILE_SIZE,
  MAX_VIDEO_FILE_DURATION,
  MAX_VIDEO_FILE_SIZE,
} from "@/constant/file";
import { MediaFile } from "@/types";
import { NamePath } from "rc-field-form/lib/interface";
import {
  useComplaintClassifyControllerGetList,
  usePostBaobPcComplaintRecordSubmit,
} from "@/openapi/baob/pc.complaint/api.client.hooks";
import { getUrlFromUploadFile } from "@/utils/help";
import { FeedbackFormRef } from "@/components/HeaderFeedback/types";
import { sendLog } from "@/utils/tracker";
import { getCurrentUser, getCurrentShopData } from "@/utils/storage";
import useQueueUpload from "@/hooks/useQueueUpload";

type PreviewMediaData = {
  src?: string;
  visible: boolean;
  title: string;
  type: "video" | "image";
};

interface FeedbackFormData {
  classifyId: string[];
  content: string;
  mobile: string;
  video?: UploadFile<MediaFile>[];
  screenshot?: UploadFile<MediaFile>[];
}

interface FeedbackFormProps {
  onSuccess?: () => void;
}

const constantMap = {
  maxFileCount: 5,
};

const FeedbackForm = (
  { onSuccess }: FeedbackFormProps,
  ref: Ref<FeedbackFormRef>,
) => {
  const currentShop = getCurrentShopData();
  const { obsUploadData } = useModel("useCommonModel");
  const { customRequest } = useQueueUpload();
  const formRef = useRef<FormInstance<FeedbackFormData>>(null);
  const form = Form.useFormInstance<FeedbackFormData>();
  const [viewerData, setViewerData] = useState<ViewerProps>(() => ({
    visible: false,
    images: [],
    activeIndex: 0,
  }));
  const [previewMediaData, setPreviewMediaData] = useState<PreviewMediaData>(
    () => ({
      visible: false,
      src: "",
      title: "",
      type: "image",
    }),
  );

  const [hevcUid, setHevcUid] = useState("");
  // 转码
  const { run: transcoding } = obsModuleApi.useTranscoding({
    manual: true,
  });

  const { data: classifyList } = useComplaintClassifyControllerGetList({
    params: {
      supply_id: currentShop?.supplierId,
    },
  });

  const { isPending: loadingSubmit, mutate: submitFeedback } =
    usePostBaobPcComplaintRecordSubmit({
      mutation: {
        onSuccess: () => {
          message.success("感谢您的反馈，祝您生意兴隆！");
          formRef.current?.resetFields();
          sendLog("pcFeedback", "意见反馈", {
            click_source: "提交",
            tab_name: "我要反馈",
          });
          onSuccess?.();
        },
      },
    });

  const handleSubmit = async (values: FeedbackFormData) => {
    const { screenshot, classifyId, video, ...rest } = values;
    const { userId, userName, roleId, roleName } = getCurrentUser();
    submitFeedback({
      supply_id: currentShop?.supplierId?.toString(),
      shopId: currentShop?.shopId,
      shopName: currentShop?.shopName,
      userId: userId,
      userName: userName,
      roleId: roleId,
      roleName: roleName,

      screenshot: getUrlFromUploadFile(screenshot).join(","),
      classifyId: classifyId[classifyId.length - 1],
      video: getUrlFromUploadFile(video)[0],
      videoCoverImg: video?.[0]?.thumbUrl,
      ...rest,
    });
  };

  const beforeUpload: UploadProps["beforeUpload"] = (file: MediaFile) => {
    const size: number = getFileSize(file);
    if (size > MAX_FILE_SIZE) {
      message.warning(`图片大小不可超过${MAX_FILE_SIZE}Mb`);
      return Upload.LIST_IGNORE;
    }
    return setFileUploadUrl(file, obsUploadData);
  };

  const beforeVideoUpload: UploadProps["beforeUpload"] = async (
    file: MediaFile,
  ) => {
    const videoFile = setFileUploadUrl(file, obsUploadData);
    const info = await getVideoCodecInfo(videoFile);
    const size: number = getFileSize(videoFile);
    if (info.durationActual > MAX_VIDEO_FILE_DURATION) {
      message.warning(`视频长度不可超过${MAX_VIDEO_FILE_DURATION}s`);
      return Upload.LIST_IGNORE;
    } else if (size > MAX_VIDEO_FILE_SIZE) {
      message.warning(`视频大小不可超过${MAX_VIDEO_FILE_SIZE}Mb`);
      return Upload.LIST_IGNORE;
    } else {
      // 如果视频尺码不存在，则是h265的编码，需要调用转码接口
      if (!isBrowserSupportCodec(info)) {
        setHevcUid(videoFile.uid);
      }
      videoFile.duration = info.durationActual;
      return videoFile;
    }
  };

  const getExtraData: UploadProps["data"] = (file) => {
    return {
      key: file.url,
      ...obsUploadData,
    };
  };

  const handlePreview = (file: UploadFile, name: NamePath) => {
    const images: ImageDecorator[] = formRef.current
      ?.getFieldValue(name)
      .map((item: UploadFile) => ({ src: item.thumbUrl, alt: "" }));
    const activeIndex = images.findIndex((item) => item.src === file.thumbUrl);
    setViewerData((s) => ({
      ...s,
      visible: true,
      images,
      activeIndex,
    }));
  };

  const handleFileChange = (
    info: UploadChangeParam<YsUploadFile<UploadFile>>,
  ) => {
    if (info.file.status === "error") {
      message.error("文件上传失败！");
    }
    setFileThumbUrl(info.file, obsUploadData);
  };

  const handleVideoPreviewFile: UploadProps["previewFile"] = (file) => {
    return Promise.resolve((file as MediaFile).thumbUrl || "");
  };

  const handleVideoPreview = async (file: UploadFile) => {
    setPreviewMediaData({
      src: getFileFullSrc(file?.url, obsUploadData),
      visible: true,
      title:
        file.name || file.url?.substring(file.url?.lastIndexOf("/") + 1) || "",
      type: "video",
    });
  };

  const handleVideoChange = async (
    info: UploadChangeParam<YsUploadFile<UploadFile>>,
  ) => {
    if (info.file.status === "error") {
      message.error("文件上传失败！");
    } else if (info.file.status === "done") {
      const fileList = info.fileList;
      // 如果是hevc（h265）格式，需要转码到 h264
      if (info.file.uid === hevcUid) {
        const module = info.file.url
          ?.replace(UPLOAD_DIR, "")
          .split("/")
          .slice(0, -1)
          .join("/");
        const res = await transcoding({
          url: info.file.wholeUrl as string,
          app_type: UPLOAD_DIR_PROJECT,
          module: module as string,
        });
        fileList[0].thumbUrl = setFileThumbUrl(
          fileList[0],
          obsUploadData,
        ).thumbUrl;
        fileList[0].wholeUrl = res.url;
        fileList[0].url = res.url?.replace(REGEXP_ORIGIN, "");
      } else {
        setFileThumbUrl(info.file, obsUploadData);
      }
      form?.setFieldsValue({ video: fileList });
    }
  };

  const handleReset = () => {
    formRef.current?.resetFields();
  };

  const action = obsUploadData?.domain;

  useImperativeHandle(ref, () => ({
    onReset: handleReset,
  }));

  return (
    <>
      <ProForm<FeedbackFormData>
        formRef={formRef}
        form={form}
        layout="horizontal"
        labelCol={{ span: 3 }}
        onFinish={handleSubmit}
        submitter={false}
      >
        <ProFormCascader
          label="反馈分类"
          name="classifyId"
          width="md"
          rules={[{ required: true, message: "反馈分类必选" }]}
          fieldProps={{
            options: classifyList?.data,
          }}
        />
        <ProFormTextArea
          name="content"
          label="反馈内容"
          placeholder="请简明描述对爆版商家管理后台的意见或使用中遇到的问题"
          fieldProps={{
            maxLength: 200,
            showCount: true,
            rows: 4,
          }}
          width="lg"
          rules={[{ required: true, message: "反馈内容必填" }]}
        />
        <ProFormUploadButton
          name="screenshot"
          label="相关截图"
          max={5}
          title="上传截图"
          fieldProps={{
            listType: "picture-card",
            accept: "image/*",
            data: getExtraData,
            onPreview: (file) => handlePreview(file, "screenshot"),
            multiple: true,
            beforeUpload,
            onChange: handleFileChange,
            customRequest,
          }}
          action={action}
          rules={[
            {
              type: "array",
              max: constantMap.maxFileCount,
              message: `最多${constantMap.maxFileCount}张`,
            },
          ]}
          extra={<span>最多{constantMap.maxFileCount}张</span>}
        />
        <ProFormUploadButton
          name="video"
          label="相关视频"
          title="上传视频"
          max={1}
          fieldProps={{
            name: "file",
            listType: "picture-card",
            accept: "video/*",
            data: getExtraData,
            beforeUpload: beforeVideoUpload,
            previewFile: handleVideoPreviewFile,
            onPreview: handleVideoPreview,
            onChange: handleVideoChange,
            customRequest,
          }}
          action={action}
          extra={
            <span>
              视频时长不超过{MAX_VIDEO_FILE_DURATION}s，建议不超过
              {MAX_VIDEO_FILE_SIZE}M
            </span>
          }
        />
        <ProFormDigit
          name="mobile"
          label="联系电话"
          placeholder="请输入电话号码"
          rules={[{ required: true, message: "联系电话必填" }]}
          width="md"
          fieldProps={{
            minLength: 11,
            maxLength: 11,
            precision: 0,
            controls: false,
            stringMode: true,
          }}
        />
        <ProFormItem label={" "} colon={false}>
          <Button loading={loadingSubmit} type="primary" htmlType="submit">
            确认提交
          </Button>
        </ProFormItem>
      </ProForm>
      <Modal
        open={previewMediaData.visible}
        title={previewMediaData.title}
        width="60%"
        footer={null}
        destroyOnClose={true}
        onCancel={() =>
          setPreviewMediaData({
            src: "",
            title: "",
            visible: false,
            type: "image",
          })
        }
      >
        {previewMediaData.type === "image" && (
          <img alt="" style={{ width: "100%" }} src={previewMediaData.src} />
        )}
        {previewMediaData.type === "video" && (
          <video
            controls={true}
            style={{ width: "100%" }}
            src={previewMediaData.src}
          />
        )}
      </Modal>
      <Viewer
        {...viewerData}
        onClose={() => {
          setViewerData((s) => ({ ...s, visible: false }));
        }}
        onMaskClick={() => {
          setViewerData((s) => ({ ...s, visible: false }));
        }}
      />
    </>
  );
};

export default forwardRef(FeedbackForm);
